/** Outdoor ambience over the world: slow bands of drifting mist, and falling petals. */
import { ambienceShare } from '../../graphics';
import { settings } from '../../settings';
import { canvas, ctx2d } from '../pixel';
import { registerRenderPass } from './registry';

interface Petal {
  x: number;
  y: number;
  v: number;
  ph: number;
}

let mist: HTMLCanvasElement | null = null;
/** A radial puff of drifting mist, 160 across each way from its middle, drawn once and kept. */
function mistPuff(): HTMLCanvasElement {
  if (mist) return mist;
  const r = 160;
  const c = canvas(r * 2, r * 2);
  const g = ctx2d(c);
  const gr = g.createRadialGradient(r, r, 0, r, r, r);
  gr.addColorStop(0, 'rgba(235,232,240,0.07)');
  gr.addColorStop(1, 'rgba(235,232,240,0)');
  g.fillStyle = gr;
  g.fillRect(0, 0, r * 2, r * 2);
  return (mist = c);
}

registerRenderPass({
  id: 'mist',
  layer: 'world',
  order: 510,
  draw(f) {
    if (!settings.ambience || f.scene) return;
    const t = f.world.time;
    // Across the part on screen, so the bands keep their place as the zoom changes.
    const v = f.visible;
    // The graphics tier's share of the ambience (graphics.ts).
    const bands = Math.ceil(3 * ambienceShare());
    for (let k = 0; k < bands; k++) {
      const y = v.y + (((k * 0.37 + 0.2) * v.h + Math.sin(t * 0.05 + k) * 20) % v.h);
      const x = v.x + ((t * (6 + k * 3) + k * 400) % (v.w + 400)) - 200;
      // The middle band of a kept radial sprite, as the gradient used to be clipped.
      f.g.drawImage(mistPuff(), 0, 100, 320, 120, x - 160, y - 60, 320, 120);
    }
  },
});

registerRenderPass({
  id: 'petals',
  layer: 'world',
  order: 520,
  state: (): Petal[] =>
    Array.from({ length: 22 }, () => ({
      x: Math.random() * 800,
      y: Math.random() * 500,
      v: 6 + Math.random() * 10,
      ph: Math.random() * 6,
    })),
  draw(f, petals) {
    if (!settings.ambience || f.scene) return;
    const { g, dt } = f;
    // Petals fall across the part on screen, wherever the zoom puts its edges.
    const v = f.visible;
    const shown = Math.ceil(petals.length * ambienceShare());
    for (const p of petals.slice(0, shown)) {
      p.x += p.v * 0.7 * dt;
      p.y += p.v * dt;
      p.ph += dt * 2;
      if (p.y > v.h + 4) {
        p.y = -4;
        p.x = Math.random() * v.w;
      }
      if (p.x > v.w + 4) p.x = -4;
      g.fillStyle = p.v > 11 ? 'rgba(246,190,206,.85)' : 'rgba(240,226,232,.55)';
      g.fillRect(Math.round(v.x + p.x + Math.sin(p.ph) * 3), Math.round(v.y + p.y), 2, 1);
    }
  },
});
